/**
 * EVERY SIGN-IN ONE HUMAN HOLDS, spelled once, for both sides of the app.
 *
 * TWO LANES, ONE FILE, AND THE MERGE THAT PUT THEM HERE. PR #1046 (messaging
 * identity) and PR #1049 (the Jr Staff report) each grew a module of this name
 * against a hub-staging that had neither, so the two APIs were concatenated
 * when the second of them merged. Nothing was dropped and nothing was rewritten
 * to fit: every export both sides defined is below, with the comments the
 * people who wrote them left. Exactly one thing was deduped, and it was
 * identical on both sides: a private STAFF_ROLES literal that spelled the same
 * five roles as JR_STAFF_ALIAS_STAFF_ROLES, which is now the one list.
 *
 * The two lanes answer two questions and are deliberately NOT collapsed:
 *
 *   1. WHICH ACCOUNTS ARE THIS HUMAN, for delivery, self-exclusion and
 *      addressing (isOwnJrStaffSignIn, jrStaffAliasUidsFor, jrStaffAliasSets,
 *      canonicalJrStaffAliasUid, jrStaffAliasCandidateWins). Built from a
 *      caller and a studentId, and its hard job is refusing a GUARDIAN who
 *      carries the same studentId.
 *   2. WHO IS ON THE JR STAFF ROSTER AND UNDER WHICH UID, for the report and
 *      its ratings (jrStaffAliasGroups, jrStaffAliasMap). Built from the whole
 *      users list plus the Director's designations, and its hard job is
 *      applying "the email account wins" so one human is one row.
 *
 * They overlap in subject and not in question, which is why isOwnJrStaffSignIn
 * and isLiveDancerLogin both survive: the first admits her active 'ensemble'
 * EMAIL account as one of her sign-ins, the second deliberately does not,
 * because lane 2 has to tell an email account from a dancer sign-in to apply
 * the precedence between them. Collapsing them would have silently changed
 * which uid a rating is filed against.
 *
 * ---- Lane 1, from PR #1046 ----
 *
 * EVERY SIGN-IN ONE HUMAN HOLDS, spelled once, for both sides of the app.
 *
 * Bobbie, 2026-09-07, on a Jr Staff who has both her own dancer sign-in and
 * an older email staff account: *"The two types of jr staff should act
 * exactly the same way, zero difference other than how they are set up"* and
 * *"Everything should reach both signs they should act exactly the same, and
 * parent should be copied on all of the junior staff correspondence."*
 *
 * A Jr Staff arrives in one of three shapes:
 *   (A) only her dancer sign-in, promoted by the parity bridge (users role
 *       'ensemble', status 'active', jrStaffPromoted true, a Firebase custom
 *       token on that same uid);
 *   (B) only an email staff account (role 'ensemble');
 *   (C) BOTH, linked by users/{uid}.studentId written on both docs.
 *
 * Shape C was two people everywhere in messaging until this module existed:
 * one alias resolver lived in jrStaffRoster.ts (jrStaffUidAliases) and was
 * imported by exactly ONE file, the class editor. Delivery, inbox scoping and
 * self-exclusion each carried their own uid and none of them knew about the
 * other account.
 *
 * WHAT THIS IS AND IS NOT. This decides IDENTITY, never PERMISSION. Grouping
 * two uids here does not let either one read anything: shared/firestore.rules
 * gates on request.auth.uid and nothing in this file changes that. It is used
 * to WIDEN a delivery list (both of her sign-ins hear it), to NARROW a
 * self-exclusion (she never hears her own post on her other sign-in), and to
 * COLLAPSE a display list (one entry per human). Every one of those is a
 * client- or server-side list decision over reads the rules already allow.
 *
 * ONE MEMBERSHIP DEFINITION, NOT A SEVENTH. isOwnJrStaffSignIn below is the
 * only answer to "which accounts are this human", and dancerPortal.mjs's
 * readAssistingClasses calls it too, so there is one place to change and one
 * place to test.
 *
 * IT IS NARROWER THAN THE RULE readAssistingClasses USED TO RUN INLINE, and
 * that narrowing is a fix, not a port. The old inline rule admitted ANY
 * active staff role carrying the dancer's studentId - but setDancerLink.mjs
 * writes studentId onto a GUARDIAN's account as well, so "everybody sharing
 * my studentId" is my sign-ins AND MY MOTHER. A Director mother matched it
 * with an active staff role on top. See isOwnJrStaffSignIn for the four
 * surfaces that were wrong because of it.
 *
 * PURE ON PURPOSE. Nothing here imports Firebase, which is what lets a
 * Netlify Function import it directly the way lib/jrStaffOfferingIdentity.mjs
 * already imports jrStaffEmailAccounts.ts, and what lets a node test load it
 * with --experimental-strip-types and no emulator.
 *
 * ---- Lane 2, from PR #1049 ----
 *
 * ONE HUMAN, ONE ENTRY, FOR A SERVER THAT CANNOT IMPORT THE CLIENT ROSTER.
 *
 * Bobbie, 2026-09-07: a Jr Staff on a dancer sign-in, on an email account, or
 * both linked is ONE person with zero difference. lib/jrStaffRoster.ts is where
 * that lives for the app, and PACKET S4's server functions need the same answer
 * before they can store a rating against the right person.
 *
 * WHY THIS FILE EXISTS RATHER THAN AN IMPORT. netlify/functions/tsconfig.json
 * typechecks with moduleResolution NodeNext and no DOM lib, and
 * jrStaffRoster.ts imports `type UserDoc` from ./users.ts, which drags the
 * Firebase client SDK, `window`, and extensionless relative imports into that
 * program. tsc resolves a type-only import exactly like any other, so importing
 * the roster from a function fails typecheck:functions outright. Proven by
 * running it, not assumed.
 *
 * SO THIS IS A DELIBERATE, DOCUMENTED LOCKSTEP, the same arrangement
 * jrStaffRoster.ts's own isPromotedDancerLogin already lives with across three
 * files for the same reason ("this file cannot import either module without
 * pulling Firebase in at load time, so the lockstep is a promise, not an
 * import"). What makes it safe here is that the promise is TESTED:
 * tests/jr-staff-checkins.test.mjs runs this derivation and
 * jrStaffUidAliases() side by side over every account shape the studio has and
 * asserts they agree, so a change to one that is not made to the other goes red.
 *
 * The precedence itself is NOT copied: jrStaffEmailAccounts() below is the same
 * shared primitive the roster and the class-identity repair both use, so the
 * one load-bearing rule ("the email account wins", Bobbie 2026-09-02) has
 * exactly one home.
 */

import { jrStaffEmailAccounts } from './jrStaffEmailAccounts.ts';

/* ============================================================================
 * LANE 1: which accounts are this human (PR #1046)
 * ==========================================================================*/

/**
 * The staff roles that make an ACTIVE account one of this human's sign-ins.
 * Byte-for-byte the list readAssistingClasses used inline, and the same list
 * isDancerLoginUserDoc (src/lib/users.ts) and userIsDancerAccount
 * (netlify/functions/lib/communityAccess.mjs) carve dancer logins out with.
 */
export const JR_STAFF_ALIAS_STAFF_ROLES: readonly string[] = [
  'director', 'manager', 'company', 'ensemble', 'foh',
];

/**
 * How many sign-ins one human may hold before this stops answering. The same
 * ceiling readAssistingClasses already enforced, for the same reason: the
 * studentId link is server-written but the query behind it is unbounded, and
 * a runaway link would turn one delivery into an unbounded fan-out.
 */
export const JR_STAFF_ALIAS_MAX = 30;

/** The fields these functions read. Narrow so a partial row still type-checks. */
export interface JrStaffAliasAccount {
  uid: string;
  role?: string | null;
  status?: string | null;
  studentId?: string | null;
  authMode?: string | null;
  jrStaffPromoted?: boolean;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * IS THIS ACCOUNT ONE OF ITS OWN DANCER'S SIGN-INS?
 *
 * THE GUARD THAT WAS MISSING, and the bug it lets through is the worst one
 * this whole branch could have shipped. users/{uid}.studentId is NOT "this
 * account is that dancer": setDancerLink.mjs writes it on a GUARDIAN's
 * account too, which is what lets one rules clause cover a parent and a
 * dancer on the family read lanes. So "every account sharing my studentId" is
 * my sign-ins AND MY MOTHER AND MY FATHER.
 *
 * Without this, three separate surfaces treated a parent as her own daughter:
 * a DM addressed to a mum was readdressed to the daughter's staff account, a
 * Director-and-parent's self-exclusion swallowed her own Jr Staff child out of
 * everything she sent, and the people picker collapsed mum and daughter into
 * one chip so the daughter could not be messaged at all.
 *
 * ASYMMETRY IS THE WHOLE POINT. Sharing a studentId is symmetric; being that
 * dancer is not. This asks the ONE question that is not symmetric: does THIS
 * account pass the sign-in membership for the studentId it carries. A parent
 * fails it (role 'family', no dancer authMode), both of a Jr Staff's own
 * accounts pass it.
 */
export function isOwnJrStaffSignIn(
  account: JrStaffAliasAccount | null | undefined
): boolean {
  if (text(account?.studentId).length === 0) return false;
  const role = text(account?.role);
  const status = text(account?.status);
  // HER OWN 'ensemble' ACCOUNT, either shape. Her email staff account is
  // active/'ensemble' (the jrStaffEmailAccounts shape), and promotion rewrites
  // her dancer sign-in to exactly the same active/'ensemble' pair on purpose -
  // that sameness IS the parity, so one line covers both.
  // WHAT THIS ADMITS, said out loud (round-9 review of PR #1038): ANY active
  // 'ensemble' account carrying a studentId is treated as that dancer's own
  // sign-in. An ADULT Jr Staff who is also a guardian and who somehow held
  // her own child's identity link would therefore be read as that child, and
  // her classes would be attributed to the child on the family surfaces that
  // use this. No such account is known to exist: identityLink is
  // Director-only, refuses a dancer already claimed by another account, and
  // is meant for the dancer's own sign-in. The narrower question that would
  // close it - is this account the dancer HERSELF rather than an adult who
  // teaches - has no client-readable answer today, which is why this reads
  // the shape it can prove instead of guessing. Recorded here rather than
  // left to be discovered.
  if (status === 'active' && role === 'ensemble') return true;
  // HER UNPROMOTED DANCER SIGN-IN. status 'dancer' is the whole
  // discriminator, and it is the one a PARENT can never wear: a guardian
  // account carrying her dancer's studentId is status 'active', role
  // 'family'. Legacy Firebase-backed dancer records carry no authMode at all
  // until they migrate, which is why this reads role and status rather than
  // the marker.
  //
  // KEPT IN LOCKSTEP BY HAND with isLiveDancerLogin in ./jrStaffRoster.ts and
  // isEnabledDancerLoginShape in netlify/functions/lib/jrStaffDancerBridge.mjs
  // - the same three-way promise that file's own header already carries.
  // 'inactive' is the switched-off half of both shapes and is refused here.
  return status === 'dancer' && !JR_STAFF_ALIAS_STAFF_ROLES.includes(role);
}

/**
 * Every account that is one of THIS dancer's own sign-ins, given a uid that
 * must itself be one of them. Empty for anybody else, which is what keeps a
 * guardian, a sibling's parent and a Director-who-is-also-a-parent out of
 * every list built from it.
 */
function ownSignInGroup(
  uid: string,
  accounts: readonly JrStaffAliasAccount[]
): { studentId: string; group: JrStaffAliasAccount[] } | null {
  const self = text(uid);
  const mine = accounts.find((account) => text(account?.uid) === self);
  if (!mine || !isOwnJrStaffSignIn(mine)) return null;
  const studentId = text(mine.studentId);
  const group = accounts.filter(
    (account) => isOwnJrStaffSignIn(account) && text(account.studentId) === studentId
  );
  return group.length > JR_STAFF_ALIAS_MAX ? null : { studentId, group };
}

/**
 * The alias set for ONE caller, from accounts the caller already holds.
 *
 * The caller's own uid is ALWAYS first and always present, even when her own
 * users doc is thin or missing: a widening that could drop the uid actually
 * signed in would be a silencing, and the failure direction here is always
 * "reaches her twice", never "reaches nobody".
 *
 * Over the ceiling, this returns her own uid ALONE rather than a truncated
 * set. Truncating would pick an arbitrary subset of one human's accounts and
 * call it complete; returning one uid is today's behaviour, unchanged, and
 * visibly not a widening.
 */
export function jrStaffAliasUidsFor(input: {
  uid: string;
  studentId?: string | null;
  accounts: readonly JrStaffAliasAccount[];
}): string[] {
  const self = text(input.uid);
  if (!self) return [];
  const studentId = text(input.studentId);
  if (!studentId) return [self];
  // THE CALLER MUST BE ONE OF THAT DANCER'S OWN SIGN-INS. A guardian carries
  // the same studentId and is NOT her - see isOwnJrStaffSignIn. Her own
  // document missing from `accounts` is answered the same way: her uid alone,
  // never a group she has not been proven to belong to.
  const mine = input.accounts.find((account) => text(account?.uid) === self);
  if (!mine || !isOwnJrStaffSignIn(mine) || text(mine.studentId) !== studentId) return [self];
  const linked = input.accounts
    .filter((account) => isOwnJrStaffSignIn(account) && text(account.studentId) === studentId)
    .map((account) => text(account.uid))
    .filter((uid) => uid.length > 0 && uid !== self)
    .sort((a, b) => a.localeCompare(b));
  if (linked.length + 1 > JR_STAFF_ALIAS_MAX) return [self];
  return [self, ...new Set(linked)];
}

/**
 * ONE CALLER'S SIGN-INS FROM TWO LOOKUPS OF THE SAME ANSWER: the browser's
 * own users query (jrStaffAliasUidsFor above, over the accounts the rules let
 * her read) and the server half of this helper (jrStaffAliasUids.mjs, which
 * can also see her UNPROMOTED dancer sign-in, a users doc the rules hide from
 * staff). Her own uid first, then every other uid either one proved, once
 * each, never more than JR_STAFF_ALIAS_MAX in all.
 *
 * A server answer that does not carry the uid actually signed in is not an
 * answer about her and is ignored whole, so a confused or stale reply can
 * never drop or replace the one sign-in that is certainly hers. Used by
 * useOwnSignInUids (./ownSignIns.ts), which draws buttons and grants nothing.
 */
export function mergeOwnSignInUids(
  uid: string,
  linked: readonly string[] | null | undefined,
  fromServer: readonly string[] | null | undefined
): string[] {
  const self = text(uid);
  if (!self) return [];
  const server = Array.isArray(fromServer) && fromServer.includes(self) ? fromServer : [];
  const merged = [...new Set([self, ...(linked ?? []), ...server].map(text).filter(Boolean))];
  return merged.length > JR_STAFF_ALIAS_MAX ? [self] : merged;
}

/**
 * EVERY UID TO EVERY UID OF THE SAME HUMAN, for a caller holding a users
 * list (a staff directory read, or the Admin SDK's own scan).
 *
 * Returns a map from each sign-in's uid to the full sorted set of uids that
 * human holds, itself included. A uid with no studentId link maps to itself
 * alone, which is the honest answer for every account that is one person with
 * one sign-in - the overwhelming majority.
 */
export function jrStaffAliasSets(
  accounts: readonly JrStaffAliasAccount[]
): Map<string, string[]> {
  const byStudentId = new Map<string, string[]>();
  for (const account of accounts) {
    const studentId = text(account?.studentId);
    if (!studentId) continue;
    // HER OWN SIGN-INS ONLY. A guardian carries the same studentId, and a
    // guardian who is also a Director carries an active staff role with it.
    if (!isOwnJrStaffSignIn(account)) continue;
    const uid = text(account.uid);
    if (!uid) continue;
    const held = byStudentId.get(studentId);
    if (held) held.push(uid);
    else byStudentId.set(studentId, [uid]);
  }
  const out = new Map<string, string[]>();
  for (const account of accounts) {
    const uid = text(account?.uid);
    if (!uid || out.has(uid)) continue;
    const studentId = text(account?.studentId);
    const group = studentId ? byStudentId.get(studentId) : undefined;
    const set = group && group.includes(uid) && group.length <= JR_STAFF_ALIAS_MAX
      ? [...new Set(group)].sort((a, b) => a.localeCompare(b))
      : [uid];
    out.set(uid, set);
  }
  return out;
}

/**
 * THE ONE UID A LIST SHOWS AND A NEW DM IS ADDRESSED TO: her email account
 * when she has one, otherwise the uid asked about.
 *
 * Bobbie, 2026-09-02: *"Keep both, email account wins: lists show the email
 * account; the dancer sign-in stays a dancer view."* Ruling 7 is a DISPLAY
 * ruling and is superseded for DELIVERY by "everything should reach both
 * signs" - which is why widening (jrStaffAliasSets) and canonicalising (this)
 * are two separate doors rather than one. A DM has a stable id minted from
 * two raw uids and a rules-checked participant list, so it must land on ONE
 * account or the same conversation splits in two; a notification has neither
 * constraint and goes to every sign-in she holds.
 *
 * The precedence itself is jrStaffEmailAccounts.ts, the module the pickers
 * and the server class repairs already share, so this is not a second answer
 * to the same question.
 */
export function canonicalJrStaffAliasUid(
  uid: string,
  accounts: readonly JrStaffAliasAccount[]
): string {
  const self = text(uid);
  if (!self) return '';
  // A PARENT IS NOT HER DAUGHTER. Without ownSignInGroup's guard this
  // readdressed a DM to a guardian straight to that dancer's Jr Staff account,
  // because the guardian's own users doc carries the same studentId.
  const own = ownSignInGroup(self, accounts);
  if (!own) return self;
  const { studentId, group } = own;
  const winner = jrStaffEmailAccounts(
    group.map((account) => ({
      uid: text(account.uid),
      role: text(account.role),
      status: text(account.status),
      studentId: text(account.studentId),
      jrStaffPromoted: account.jrStaffPromoted === true,
    })),
    studentId
  )[0];
  return winner?.uid || self;
}

/**
 * THE EMAIL ACCOUNT WINS, spelled once for both dedupe passes that need it.
 *
 * oneEntryPerHuman (src/lib/jrStaffRoster.ts) and
 * assistantNamesForOfferingData (netlify/functions/dancerPortal.mjs) each
 * carried their own copy of this tiebreak: "already holding this human, swap
 * only if what I am holding is her dancer sign-in and the candidate is not."
 * Two copies of one ruling is how the two lists disagree about a person.
 */
export function jrStaffAliasCandidateWins(
  heldIsDancerSignIn: boolean,
  candidateIsDancerSignIn: boolean
): boolean {
  return heldIsDancerSignIn && !candidateIsDancerSignIn;
}

/* ============================================================================
 * LANE 2: who is on the Jr Staff roster, and under which uid (PR #1049)
 * ==========================================================================*/

/** The minimum any account needs to carry for this file to place it. */
export interface AliasAccount {
  uid: string;
  role: string;
  status: string;
  studentId?: string | null;
  jrStaffPromoted?: boolean;
}

/**
 * KEPT IN LOCKSTEP BY HAND with isPromotedDancerLogin in
 * hub/src/lib/jrStaffRoster.ts and in
 * netlify/functions/lib/jrStaffDancerBridge.mjs. Deliberately the same
 * marker-plus-role test and deliberately NOT an authMode test: legacy
 * Firebase-backed dancer records carry no authMode until they migrate.
 */
function isPromotedDancerLogin(user: AliasAccount): boolean {
  if (typeof user.studentId !== 'string' || user.studentId.trim().length === 0) return false;
  return user.jrStaffPromoted === true && user.role === 'ensemble';
}

function isDancerLoginShaped(user: AliasAccount): boolean {
  if (typeof user.studentId !== 'string' || user.studentId.trim().length === 0) return false;
  if (isPromotedDancerLogin(user)) return true;
  return !JR_STAFF_ALIAS_STAFF_ROLES.includes(user.role);
}

/** Live: an unpromoted login at status 'dancer', a promoted one at 'active'. */
function isLiveDancerLogin(user: AliasAccount): boolean {
  if (!isDancerLoginShaped(user)) return false;
  return isPromotedDancerLogin(user) ? user.status === 'active' : user.status === 'dancer';
}

export interface JrStaffAliasGroup {
  studentId: string | null;
  /** The uid every list shows: her email account first, her sign-in second. */
  accountUid: string;
  /** Her other uids, which fold onto accountUid everywhere. */
  duplicateUids: string[];
}

/**
 * Every Jr Staff human who HAS an account, one group each.
 *
 * @param designatedStudentIds the dancers a Director tagged as Jr Staff
 *   (students/{id}.jrStaffDesignated). A dancer with her own live sign-in and a
 *   designation is Jr Staff even with no ensemble account of her own; an active
 *   ensemble account is Jr Staff by its role alone and needs no designation.
 */
export function jrStaffAliasGroups<T extends AliasAccount>(
  users: readonly T[],
  designatedStudentIds: ReadonlySet<string> = new Set()
): JrStaffAliasGroup[] {
  // A Netlify function cannot import jrStaffRoster.ts: its type-only import of
  // users.ts drags the Firebase web SDK and window into typecheck:functions,
  // proven by running it. So this is the by-hand lockstep that module's own
  // header already describes, and what makes it safe rather than a second
  // answer is that tests/jr-staff-checkins.test.mjs runs THIS derivation and
  // jrStaffUidAliases side by side over every account shape and asserts they
  // agree, so a change to one that is not made to the other goes red.
  // jr-staff-ok: the by-hand lockstep for a function that cannot import the
  // shared module, and the lockstep is tested rather than promised.
  const activeEnsemble = users.filter((user) => user.status === 'active' && user.role === 'ensemble');
  const linkedStudentIds = new Set<string>();
  for (const user of activeEnsemble) {
    if (typeof user.studentId === 'string' && user.studentId) linkedStudentIds.add(user.studentId);
  }
  const studentIds = [...new Set([...designatedStudentIds, ...linkedStudentIds])].sort((a, b) =>
    a.localeCompare(b)
  );

  const groups: JrStaffAliasGroup[] = [];
  const claimed = new Set<string>();

  for (const studentId of studentIds) {
    const forStudent = [...users]
      .filter((user) => user.studentId === studentId)
      .sort((a, b) => a.uid.localeCompare(b.uid));
    const liveLogins = forStudent.filter(isLiveDancerLogin);
    const emailAccounts = jrStaffEmailAccounts(forStudent, studentId);
    // THE EMAIL ACCOUNT WINS (Bobbie, 2026-09-02: "Keep both, email account
    // wins: lists show the email account; the dancer sign-in stays a dancer
    // view"). Nothing is merged or deleted; the other uids only fold onto it.
    const account = emailAccounts[0] ?? liveLogins[0] ?? null;
    if (!account) continue;
    const duplicateUids = [
      ...emailAccounts.filter((user) => user.uid !== account.uid).map((user) => user.uid),
      ...liveLogins.filter((user) => user.uid !== account.uid).map((user) => user.uid),
    ];
    claimed.add(account.uid);
    for (const uid of duplicateUids) claimed.add(uid);
    groups.push({ studentId, accountUid: account.uid, duplicateUids });
  }

  // An ACTIVE ensemble account with NO identity link is still Jr Staff: the
  // Director gave it the role, and roughly two dozen accounts predate the
  // identity-link lane entirely.
  for (const user of activeEnsemble) {
    if (claimed.has(user.uid)) continue;
    if (typeof user.studentId === 'string' && user.studentId.length > 0) continue;
    groups.push({ studentId: null, accountUid: user.uid, duplicateUids: [] });
  }

  return groups;
}

/** Every uid a human holds, pointing at the one the lists show. */
export function jrStaffAliasMap<T extends AliasAccount>(
  users: readonly T[],
  designatedStudentIds: ReadonlySet<string> = new Set()
): Map<string, string> {
  const aliases = new Map<string, string>();
  for (const group of jrStaffAliasGroups(users, designatedStudentIds)) {
    aliases.set(group.accountUid, group.accountUid);
    for (const uid of group.duplicateUids) aliases.set(uid, group.accountUid);
  }
  return aliases;
}
